import { useState, useMemo } from "react";
import { ChevronDown, Check, Search } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { cn } from "../lib/utils";

/**
 * Checkbox-style multi-select filter.
 *
 * `options` is [{ value, label, hint? }]. `value` is the selected array and
 * `onChange` receives the next one. Picking an option keeps the menu open so
 * several can be ticked in one pass, and the trigger shows a count rather than
 * a truncated list of labels — a count stays readable at any selection size.
 *
 * A search box appears once the list is long enough to be worth filtering.
 */
export default function FilterMultiSelect({
  label,
  options,
  value = [],
  onChange,
  searchThreshold = 8,
  className,
}) {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const toggle = (optionValue) =>
    onChange(
      value.includes(optionValue)
        ? value.filter((v) => v !== optionValue)
        : [...value, optionValue]
    );

  const count = value.length;
  const allSelected = count > 0 && count === options.length;
  const showSearch = options.length >= searchThreshold;

  return (
    <DropdownMenu onOpenChange={(open) => !open && setQuery("")}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "h-9 gap-2 bg-card font-normal",
            count > 0 && "border-primary/40 bg-primary/5 text-foreground",
            className
          )}
        >
          <span>{label}</span>
          {count > 0 && (
            <span className="flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-semibold tabular-nums text-primary-foreground">
              {count}
            </span>
          )}
          <ChevronDown className="h-3.5 w-3.5 opacity-50" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="w-64 p-0">
        {showSearch && (
          <div className="border-b border-border p-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                placeholder={`Filter ${label.toLowerCase()}…`}
                className="h-8 pl-8 text-sm"
              />
            </div>
          </div>
        )}

        <div className="max-h-64 overflow-y-auto p-1">
          {filtered.length === 0 ? (
            <p className="px-2 py-6 text-center text-sm text-muted-foreground">No matches.</p>
          ) : (
            filtered.map((option) => {
              const checked = value.includes(option.value);
              return (
                <DropdownMenuItem
                  key={option.value}
                  onSelect={(e) => {
                    e.preventDefault(); // keep open for multiple picks
                    toggle(option.value);
                  }}
                  className="gap-2.5 py-2"
                >
                  <span
                    className={cn(
                      "flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors",
                      checked
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-input bg-background"
                    )}
                  >
                    {checked && <Check className="h-3 w-3" strokeWidth={3} />}
                  </span>
                  <span className="flex-1">{option.label}</span>
                  {option.hint && (
                    <span className="text-xs text-muted-foreground">{option.hint}</span>
                  )}
                </DropdownMenuItem>
              );
            })
          )}
        </div>

        <DropdownMenuSeparator className="my-0" />
        <div className="flex items-center justify-between p-1">
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              onChange(allSelected ? [] : options.map((o) => o.value));
            }}
            className="flex-1 justify-center text-xs font-medium"
          >
            {allSelected ? "Deselect all" : "Select all"}
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={count === 0}
            onSelect={(e) => {
              e.preventDefault();
              onChange([]);
            }}
            className={cn(
              "flex-1 justify-center text-xs font-medium",
              count === 0 && "pointer-events-none opacity-40"
            )}
          >
            Clear
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
